import React, { useEffect, useState, useMemo } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useSite } from "../store/useSite";
import { getDashboardContextValue } from "../utils/dashboardContext";
import { useAuth } from "../store/useAuth";
import { AddHallModal } from "./AddHallModal";
import { getHalls } from "../services/zone";
import { useTimezoneStore } from "../store/useTimezoneStore";
import {
  formatInTimezone,
  computeEventUtcInstant,
  getEventStartEndUtc,
} from "../utils/timezoneFormatter";
import { CustomSelect } from "./common/CustomSelect";
import { Plus } from "lucide-react";

const NAME_MAX = 255;
const ZONE_MODAL_TOAST_ID = "zone-modal-toast";
const zoneModalToastOptions = {
  containerId: ZONE_MODAL_TOAST_ID,
};

function toDateInputValue(iso, tz) {
  if (!iso) return "";
  const targetTz = tz || useTimezoneStore.getState().getActiveTimezone();
  return formatInTimezone(iso, targetTz, "YYYY-MM-DD");
}

function toTimeInputValue(iso, tz) {
  if (!iso) return "";
  const targetTz = tz || useTimezoneStore.getState().getActiveTimezone();
  return formatInTimezone(iso, targetTz, "HH:mm");
}

function combineLocal(dateStr, timeStr, tz) {
  if (!dateStr || !timeStr) return "";
  const targetTz = tz || useTimezoneStore.getState().getActiveTimezone();
  const normalizedTime = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
  const instant = computeEventUtcInstant(dateStr, normalizedTime, targetTz);
  return instant ? instant.toISOString() : new Date(`${dateStr}T${timeStr}:00Z`).toISOString();
}

export function AddZoneModal({ open, onClose, onSave, initial, onCreateHall }) {
  const { eventData } = useSite();
  const localTz = useTimezoneStore.getState().getActiveTimezone();

  const { startUtc: eventStartUtc, endUtc: eventEndUtc } = useMemo(
    () => getEventStartEndUtc(eventData),
    [eventData]
  );

  const eventStartDate = useMemo(() => {
    if (eventStartUtc) return formatInTimezone(eventStartUtc, localTz, "YYYY-MM-DD");
    if (eventData?.start_date) return toDateInputValue(eventData.start_date, localTz);
    return "";
  }, [eventStartUtc, eventData, localTz]);

  const eventStartTime = useMemo(() => {
    if (eventStartUtc) return formatInTimezone(eventStartUtc, localTz, "HH:mm");
    if (eventData?.start_time) return String(eventData.start_time).slice(0, 5);
    return "";
  }, [eventStartUtc, eventData, localTz]);

  const eventEndDate = useMemo(() => {
    if (eventEndUtc) return formatInTimezone(eventEndUtc, localTz, "YYYY-MM-DD");
    if (eventData?.end_date) return toDateInputValue(eventData.end_date, localTz);
    return eventStartDate || "";
  }, [eventEndUtc, eventData, eventStartDate, localTz]);

  const eventEndTime = useMemo(() => {
    if (eventEndUtc) return formatInTimezone(eventEndUtc, localTz, "HH:mm");
    if (eventData?.end_time) return String(eventData.end_time).slice(0, 5);
    return "";
  }, [eventEndUtc, eventData, localTz]);

  const [name, setName] = useState(initial?.name ?? "");
  const [location, setLocation] = useState(initial?.location ?? "");

  const [openDate, setOpenDate] = useState(() => toDateInputValue(initial?.opensAt, localTz) || eventStartDate || "");
  const [openTime, setOpenTime] = useState(() => toTimeInputValue(initial?.opensAt, localTz) || eventStartTime || "09:00");
  const [closeDate, setCloseDate] = useState(() => toDateInputValue(initial?.closesAt, localTz) || eventEndDate || eventStartDate || "");
  const [closeTime, setCloseTime] = useState(() => toTimeInputValue(initial?.closesAt, localTz) || eventEndTime || "18:00");

  const [saving, setSaving] = useState(false);
  const [hallOpen, setHallOpen] = useState(false);

  const [allowMultipleEntry, setAllowMultipleEntry] = useState(Number(initial?.multiple_entry) === 1);
  const [requiredCheckOut, setRequiredCheckOut] = useState(Number(initial?.required_check_out) === 1);

  const [halls, setHalls] = useState([]);
  const [hallLoading, setHallLoading] = useState(false);
  const [selectedHallId, setSelectedHallId] = useState(null);

  const authUser = useAuth((state) => state.user);
  const authData = useAuth((state) => state.authData);
  const authEventId = useAuth((state) => state.eventId);
  const contextEventId = getDashboardContextValue("eventId");
  const eventId = authEventId || contextEventId || eventData?.id || "";
  const token = authUser?.token || authData?.token || useAuth.getState().user?.token || "";

  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? "");
    setLocation(initial?.location ?? "");
    setOpenDate(toDateInputValue(initial?.opensAt, localTz) || eventStartDate || "");
    setOpenTime(toTimeInputValue(initial?.opensAt, localTz) || eventStartTime || "09:00");
    setCloseDate(toDateInputValue(initial?.closesAt, localTz) || eventEndDate || eventStartDate || "");
    setCloseTime(toTimeInputValue(initial?.closesAt, localTz) || eventEndTime || "18:00");
    setSelectedHallId(initial?.hall_id ? String(initial.hall_id) : null);
    setAllowMultipleEntry(Number(initial?.multiple_entry) === 1);
    setRequiredCheckOut(Number(initial?.required_check_out) === 1);
  }, [open, initial, localTz, eventStartDate, eventStartTime, eventEndDate, eventEndTime]);

  const fetchHalls = async () => {
    if (!eventId) return;
    try {
      setHallLoading(true);
      const res = await getHalls(eventId, token);
      const rows = res.data?.halls || res.data?.data?.halls || res.data?.data || res.data || [];
      const arr = Array.isArray(rows) ? rows : [];
      setHalls(arr);

      if (!initial?.hall_id && initial?.location) {
        const m = arr.find(
          (h) => (h.name || "").toLowerCase() === initial.location.toLowerCase()
        );
        if (m) setSelectedHallId(String(m.id));
      }
    } catch {
      toast.error("Failed to load halls", zoneModalToastOptions);
    } finally {
      setHallLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchHalls();
  }, [open]);

  if (!open) return null;

  const savingDisabled =
    !name.trim() || !openDate || !openTime || !closeDate || !closeTime;

  const hallOptions = halls.map((h) => ({
    value: String(h.id),
    label: h.name,
    subtitle: h.address || "",
  }));

  const handleOpenDateChange = (val) => {
    setOpenDate(val);
    if (closeDate && val > closeDate) {
      setCloseDate(val);
    }
    if (val === eventStartDate && eventStartTime && openTime && openTime < eventStartTime) {
      setOpenTime(eventStartTime);
    }
    if (val === eventEndDate && eventEndTime && openTime && openTime > eventEndTime) {
      setOpenTime(eventEndTime);
    }
  };

  const handleCloseDateChange = (val) => {
    setCloseDate(val);
    if (val === eventEndDate && eventEndTime && closeTime && closeTime > eventEndTime) {
      setCloseTime(eventEndTime);
    }
    if (openDate && val === openDate && closeTime && openTime && closeTime < openTime) {
      setCloseTime(openTime);
    }
  };

  const handleOpenTimeChange = (val) => {
    let nextVal = val;
    if (openDate === eventStartDate && eventStartTime && nextVal && nextVal < eventStartTime) {
      nextVal = eventStartTime;
    }
    if (openDate === eventEndDate && eventEndTime && nextVal && nextVal > eventEndTime) {
      nextVal = eventEndTime;
    }
    setOpenTime(nextVal);
    if (openDate && closeDate && openDate === closeDate && nextVal > closeTime) {
      setCloseTime(nextVal);
    }
  };

  const handleCloseTimeChange = (val) => {
    let nextVal = val;
    const minClose = (openDate && closeDate && openDate === closeDate)
      ? openTime
      : (closeDate === eventStartDate && eventStartTime ? eventStartTime : "");
    if (minClose && nextVal && nextVal < minClose) {
      nextVal = minClose;
    }
    if (closeDate === eventEndDate && eventEndTime && nextVal && nextVal > eventEndTime) {
      nextVal = eventEndTime;
    }
    setCloseTime(nextVal);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <ToastContainer
        containerId={ZONE_MODAL_TOAST_ID}
        position="top-center"
        autoClose={3000}
        style={{ zIndex: 100000 }}
      />
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl my-4 sm:my-8 max-h-[85vh] flex flex-col border border-gray-100">
        <div className="relative border-b border-gray-100 px-5 sm:px-6 py-4 sticky top-0 bg-white z-10 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {initial?.id ? "Edit Zone" : "Create Zone"}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Set zone information and timing
            </p>
          </div>
          <button
            type="button"
            aria-label="close"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="grid gap-5 px-5 sm:px-6 py-5">
            <div className="relative">
              <label className="mb-1.5 block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                Name <span className="text-red-600">*</span>
              </label>
              <input
                className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm outline-none focus:ring-1 focus:ring-[#152736] focus:border-[#152736]"
                value={name}
                onChange={(e) =>
                  e.target.value.length <= NAME_MAX && setName(e.target.value)
                }
                placeholder="e.g. Main Auditorium, VIP Lounge"
              />
              <div className="pointer-events-none absolute right-2.5 top-0 text-[11px] text-gray-400">
                {name.length} / {NAME_MAX}
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                Location / Hall <span className="text-red-600">*</span>
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1">
                  <CustomSelect
                    options={hallOptions}
                    value={selectedHallId ?? ""}
                    onChange={(val) => {
                      setSelectedHallId(val);
                      const found = halls.find((h) => String(h.id) === String(val));
                      setLocation(found ? found.name : "");
                    }}
                    placeholder={hallLoading ? "Loading halls..." : "Select a hall"}
                    disabled={hallLoading}
                  />
                </div>

                <button
                  type="button"
                  onClick={() => setHallOpen(true)}
                  className="inline-flex items-center justify-center gap-1.5 rounded-md border border-gray-300 bg-white px-3.5 py-2 text-xs font-semibold uppercase tracking-wide text-gray-700 hover:bg-gray-50 whitespace-nowrap shadow-xs"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Add Hall</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="grid gap-1.5">
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Open Date <span className="text-red-600">*</span>
                </span>
                <input
                  type="date"
                  min={eventStartDate || undefined}
                  max={eventEndDate || undefined}
                  className="h-10 rounded-md border border-gray-300 px-3 text-sm outline-none focus:ring-1 focus:ring-[#152736] focus:border-[#152736]"
                  value={openDate}
                  onChange={(e) => handleOpenDateChange(e.target.value)}
                />
              </label>
              <label className="grid gap-1.5">
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Open Time <span className="text-red-600">*</span>
                </span>
                <input
                  type="time"
                  min={openDate === eventStartDate && eventStartTime ? eventStartTime : undefined}
                  max={openDate === eventEndDate && eventEndTime ? eventEndTime : undefined}
                  className="h-10 rounded-md border border-gray-300 px-3 text-sm outline-none focus:ring-1 focus:ring-[#152736] focus:border-[#152736]"
                  value={openTime}
                  onChange={(e) => handleOpenTimeChange(e.target.value)}
                />
              </label>
              <label className="grid gap-1.5">
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Close Date <span className="text-red-600">*</span>
                </span>
                <input
                  type="date"
                  min={openDate || eventStartDate || undefined}
                  max={eventEndDate || undefined}
                  className="h-10 rounded-md border border-gray-300 px-3 text-sm outline-none focus:ring-1 focus:ring-[#152736] focus:border-[#152736]"
                  value={closeDate}
                  onChange={(e) => handleCloseDateChange(e.target.value)}
                />
              </label>
              <label className="grid gap-1.5">
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Close Time <span className="text-red-600">*</span>
                </span>
                <input
                  type="time"
                  min={
                    openDate && closeDate && openDate === closeDate
                      ? openTime
                      : closeDate === eventStartDate && eventStartTime
                        ? eventStartTime
                        : undefined
                  }
                  max={closeDate === eventEndDate && eventEndTime ? eventEndTime : undefined}
                  className="h-10 rounded-md border border-gray-300 px-3 text-sm outline-none focus:ring-1 focus:ring-[#152736] focus:border-[#152736]"
                  value={closeTime}
                  onChange={(e) => handleCloseTimeChange(e.target.value)}
                />
              </label>
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 border-t border-gray-100 px-5 sm:px-6 py-4 sticky bottom-0 bg-white">
          <button
            type="button"
            className="h-10 rounded-md border border-gray-300 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="h-10 rounded-md bg-[#123047] px-6 text-sm font-semibold text-white hover:bg-[#0b2235] disabled:opacity-60 transition-colors"
            onClick={async () => {
              if (savingDisabled) {
                if (!name.trim()) toast.error("Name is required", zoneModalToastOptions);
                else toast.error("Open/close date and time required", zoneModalToastOptions);
                return;
              }
              if (!selectedHallId) {
                toast.error("Please select a hall", zoneModalToastOptions);
                return;
              }

              const opensAtISO = combineLocal(openDate, openTime, localTz);
              const closesAtISO = combineLocal(closeDate, closeTime, localTz);
              const openInstant = new Date(opensAtISO);
              const closeInstant = new Date(closesAtISO);

              if (closeInstant <= openInstant) {
                toast.error("Close date and time must be after open date and time", zoneModalToastOptions);
                return;
              }

              if (eventStartUtc && openInstant < eventStartUtc) {
                toast.error(
                  `Open time cannot be before event start (${eventStartDate} ${eventStartTime || "00:00"})`,
                  zoneModalToastOptions
                );
                return;
              }

              if (eventEndUtc && closeInstant > eventEndUtc) {
                toast.error(
                  `Close time cannot be after event end (${eventEndDate} ${eventEndTime || "23:59"})`,
                  zoneModalToastOptions
                );
                return;
              }

              try {
                setSaving(true);
                await onSave({
                  id: initial?.id,
                  name: name.trim(),
                  hall_id: selectedHallId ? parseInt(selectedHallId, 10) : null,
                  opensAt: opensAtISO,
                  closesAt: closesAtISO,
                  access: initial?.access || [{ role: "all" }],
                  audience_type: initial?.audience_type || "registrations",
                  audience_config: initial?.audience_config || null,
                  multiple_entry: allowMultipleEntry ? 1 : 0,
                  required_check_out: requiredCheckOut && allowMultipleEntry ? 1 : 0,
                });
                onClose();
              } finally {
                setSaving(false);
              }
            }}
            disabled={saving}
          >
            {saving ? "Saving..." : initial?.id ? "Update Zone" : "Create Zone"}
          </button>
        </div>
      </div>

      <AddHallModal
        open={hallOpen}
        onClose={() => setHallOpen(false)}
        onSave={async ({ name, address }) => {
          const created = await onCreateHall({ name, address });
          await fetchHalls();
          const id = String(created?.id || "");
          if (id) {
            setSelectedHallId(id);
            setLocation(created?.name || name || "");
          }
          return created;
        }}
      />
    </div>
  );
}

export default AddZoneModal;
